import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { Select } from "components/select/";
import DataTable from "components/dataTable/DataTable";

import { useTranslation } from "react-i18next";
import Toggle from "components/form/toggle/Toggle";

import { isEqualWith, isNull } from "lodash";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { downloadCsv } from "helpers/Helpers";
import { GetRegisters, PutRegister } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { GetRegisterGroups } from "api/rpc/2024-04/facilityAdmin/facility/register/group";
import { GetCardReaders } from "api/rpc/2024-04/facilityAdmin/payment/teeonPayments/reader";
import { GetRegisterInventory, TRegisterInventoryLevel } from "api/rpc/2024-04/facilityAdmin/product/registerInventory";
import { GetVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";
import RegisterStockTransferModal from "./RegisterStockTransferModal";

type TRegisterStockRow = TRegisterInventoryLevel & {
  product_title?: string;
  variant_title?: string;
  sku?: string;
  barcode?: string;
};

export default function Register() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Option } = Select;
  const { registerId }: any = useParams();
  const NONE_CARD_READER_ID = -1;
  const [state, setState] = useState({
    register: null,
    register_title: "",
    register_group_id: null,
    register_groups: [],
    default_card_reader_id: null,
    readers: [],
    auto_log_out: false,
    default_print_receipt: false,
    send_kitchen: false,
    disabled: false,
  });
  const [registerStateBeforeChanges, setRegisterStateBeforeChanges] = useState<
    | {
      register: any;
      register_title: string;
      register_group_id: any;
      register_groups: any[];
      default_card_reader_id: any;
      readers: any[];
      auto_log_out: boolean;
      default_print_receipt: boolean;
      send_kitchen: boolean;
      disabled: boolean;
    }
    | undefined
  >(undefined);

  const [registerLoaded, setRegisterLoaded] = useState<boolean>(false);
  const [registerGroupLoaded, setRegisterGroupLoaded] = useState<boolean>(false);
  const [readerLoaded, setReaderLoaded] = useState<boolean>(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [stockRows, setStockRows] = useState<TRegisterStockRow[]>([]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadRegister();
    void loadRegisterGroups();
    void loadReaders();
    void loadRegisterStock(source.token);
    return () => source.cancel();
  }, []);

  async function loadRegister() {
    const res = await GetRegisters({ id: registerId, active_shift: true }, true);
    if (res.status !== StatusCode.OK) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      register: res.data[0],
      register_title: res.data[0].title,
      default_card_reader_id:
        res.data[0].default_card_reader_id === null ? NONE_CARD_READER_ID : res.data[0].default_card_reader_id,
      register_group_id: res.data[0].register_group_id,
      auto_log_out: res.data[0]?.auto_log_out,
      send_kitchen: res.data[0]?.send_kitchen,
      default_print_receipt: res.data[0]?.print_by_default,
      disabled: res.data[0]?.disabled,
    }));

    setRegisterLoaded(true);
  }

  async function loadRegisterGroups() {
    const res = await GetRegisterGroups(undefined, false);
    if (res.status !== StatusCode.OK) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      register_groups: res.data,
    }));

    setRegisterGroupLoaded(true);
  }

  async function loadReaders() {
    const res = await GetCardReaders(undefined, false);
    if (res.status !== StatusCode.OK) {
      return;
    }

    const cardReaderDeviceTypes = [
      "bbpos_wisepos_e",
      "bbpos_wisepad_3",
      "stripe_s700",
      "stripe_s710",
      "simulated_wisepos_e",
      "simulated_stripe_s700",
      "simulated_stripe_s710",
    ];
    const cardReaders = res.data?.filter((reader: any) => cardReaderDeviceTypes.includes(reader.device_type as string));
    setState(prevState => ({ ...prevState, readers: cardReaders }));

    setReaderLoaded(true);
  }

  async function loadRegisterStock(token?: CancelToken) {
    if (!registerId) {
      setStockRows([]);
      return;
    }

    const res = await GetRegisterInventory({ register_id: Number(registerId) }, false, token);
    if (token?.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting register inventory")));
      setStockRows([]);
      return;
    }

    const levels = (res.data ?? []).filter(level => Number(level.quantity_available) !== 0);
    if (levels.length === 0) {
      setStockRows([]);
      return;
    }

    const variantResponses = await Promise.all(
      levels.map(level => GetVariant({ id: level.variant_id, product: true }, false, token)),
    );
    if (token?.reason) {
      return;
    }

    setStockRows(
      levels.map((level, index) => {
        const variant = variantResponses[index]?.status === StatusCode.OK ? variantResponses[index].data?.[0] : undefined;
        return {
          ...level,
          product_title: variant?.product?.preferred_title || variant?.product?.title,
          variant_title: variant?.title,
          sku: variant?.sku,
          barcode: variant?.barcode,
        };
      }),
    );
  }

  async function saveRegister() {
    const params = {
      id: state.register.id,
      register_group_id: state.register_group_id === null ? null : state.register_group_id,
      title: state.register_title,
      default_card_reader_id:
        state.default_card_reader_id === NONE_CARD_READER_ID ? null : state.default_card_reader_id,
      auto_log_out: state.auto_log_out,
      send_kitchen: state.send_kitchen,
      print_by_default: state.default_print_receipt,
      disabled: state?.disabled,
    };

    const updateRes = await PutRegister(params, true);
    if (updateRes.status !== StatusCode.OK) {
      return;
    }

    const selectedRegister = JSON.parse(localStorage.getItem("register"));

    if (state?.register?.id === selectedRegister?.id) {
      const registerAsString = JSON.stringify(updateRes?.data);
      if (registerAsString) {
        localStorage.setItem("register", registerAsString);
      }
    }

    history.push("/admin/settings/registers");
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDropDownChange(value: any, property: string) {
    setState(prev => ({ ...prev, [property]: value }));
  }

  function handleToggleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = e.target;
    setState(prevState => ({ ...prevState, [id]: checked }));
  }

  function unsavedChangesExist() {
    if (registerStateBeforeChanges === undefined) {
      if (registerLoaded && registerGroupLoaded && readerLoaded) {
        setRegisterStateBeforeChanges(state);
      }
      return false;
    }

    return !isEqualWith(registerStateBeforeChanges, state, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function cancelUnsavedChanges() {
    setState(registerStateBeforeChanges);
  }

  function exportRegisterInventory() {
    const headers = [t("Title"), t("Sku"), t("Barcode"), t("Register Quantity"), t("Facility Quantity")];
    const rows = (stockRows ?? []).map(row => {
      const title = `${row.product_title || row.variant_id}${row.variant_title ? ` - ${row.variant_title}` : ""}`;
      return [
        title,
        row.sku ?? "",
        row.barcode ?? "",
        row.quantity_available,
        row.facility_quantity_available ?? "",
      ];
    });
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const registerTitle = String(state.register?.title ?? "register")
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_|_$/g, "")
      .toLowerCase();
    downloadCsv(csv, `register_inventory_${registerTitle || "register"}.csv`);
  }

  return (
    <Page
      title={state.register?.title}
      narrow
      //Translation Needed
      breadcrumbs={[{ prefix: true, label: t("Registers"), url: "/admin/settings/registers" }]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveRegister,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <Card title={t("Register Options")}>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={state.register_title}
                label={t("Title")}
                id="register_title"
                onChange={handleInputChange}
                placeholder={t("Title")}
              />
              <Select
                label={t("Register Groups")}
                onChange={(value: any) => handleDropDownChange(value, "register_group_id")}
                defaultValue={state.register_group_id}
              >
                {state.register_groups.map((registerGroup: any, index: number) => {
                  return (
                    <Option key={index} value={registerGroup.id} name={registerGroup.title}>
                      {registerGroup.title}
                    </Option>
                  );
                })}
              </Select>

              {/* Translation Needed */}
              <Select
                label={t("Default Card Reader")}
                onChange={(value: any) => handleDropDownChange(value, "default_card_reader_id")}
                defaultValue={state.default_card_reader_id}
              >
                <Option value={NONE_CARD_READER_ID} name={t("None")}>
                  {t("None")}
                </Option>
                {state.readers.map((reader: any, index: number) => {
                  return (
                    <Option key={index} value={reader.id} name={reader.label}>
                      {reader.label}
                    </Option>
                  );
                })}
              </Select>
            </FormLayout.Group>

            <div className="mt-4">
              <FormLayout.Group>
                <Toggle
                  id={"auto_log_out"}
                  labelTop={t("Logout After Transaction")}
                  onChange={e => handleToggleChange(e)}
                  checked={state.auto_log_out}
                />

                <Toggle
                  id={"default_print_receipt"}
                  labelTop={t("Print Receipt by Default")}
                  onChange={e => handleToggleChange(e)}
                  checked={state.default_print_receipt}
                />

                <Toggle
                  id={"send_kitchen"}
                  labelTop={"Send Kitchen"}
                  onChange={e => handleToggleChange(e)}
                  checked={state.send_kitchen}
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <Toggle
                  id={"disabled"}
                  labelTop={t("Disable")}
                  onChange={e => handleToggleChange(e)}
                  checked={state?.disabled}
                />
              </FormLayout.Group>
            </div>
          </FormLayout>
        </Card.Section>
      </Card>

      <Card
        title={t("Register Inventory")}
        titleActions={[
          {
            content: t("Transfer Inventory"),
            action: () => setTransferModalOpen(true),
              buttonType: "primary",
            disabled: !state.register?.id,
          },
          {
            content: t("Export"),
            action: exportRegisterInventory,
            buttonType: "secondary",
            disabled: !stockRows?.length,
          },
        ]}
      >
        <Card.Section>
          <DataTable
            columns={[
              { label: t("Title") },
              { label: t("Sku") },
              { label: t("Barcode") },
              { label: t("Register Quantity") },
              { label: t("Facility Quantity") },
            ]}
            loading={stockRows === undefined}
          >
            {stockRows?.map(row => (
              <tr key={`${row.register_id}-${row.variant_id}`}>
                <td>
                  {row.product_title || `#${row.variant_id}`}
                  {row.variant_title ? ` - ${row.variant_title}` : ""}
                </td>
                <td>{row.sku}</td>
                <td>{row.barcode}</td>
                <td>{row.quantity_available}</td>
                <td>{row.facility_quantity_available ?? ""}</td>
              </tr>
            ))}
            {stockRows?.length < 1 && <tr>
              <td colSpan={5} className="text-center">{t("No inventory at this register")}</td>
            </tr>}
          </DataTable>
        </Card.Section>
      </Card>

      <RegisterStockTransferModal
        open={transferModalOpen}
        registerId={state.register?.id}
        registerTitle={state.register?.title}
        onCancel={() => setTransferModalOpen(false)}
        onSuccess={() => {
          setTransferModalOpen(false);
          void loadRegisterStock();
        }}
      />
    </Page>
  );
}
